import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import {
  Link as LinkIcon,
  Check as CheckIcon,
  CompareArrows as CompareArrowsIcon,
  Sort as SortIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { FallbackAvatar } from '../../../../../general/user-profile/components/Avatar';
import { GET_MATCHING_CANDIDATES, GET_APPLICANT_AVAILABILITY } from './matching-panel-model.gql';

const MATCHING_ROLE_IDS = ['3a957d71-6452-487b-8024-318992f585f4', '7fa5f3df-8002-4c86-aa02-f1d95337766e'];

function getCounterpartRoleId(applicantRoleId: string): string | null {
  if (applicantRoleId === MATCHING_ROLE_IDS[0]) return MATCHING_ROLE_IDS[1];
  if (applicantRoleId === MATCHING_ROLE_IDS[1]) return MATCHING_ROLE_IDS[0];
  return null;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function calculateAge(dateOfBirth: string | null | undefined): number | null {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

interface SimpleAvailability {
  mondayAm: boolean;
  mondayPm: boolean;
  tuesdayAm: boolean;
  tuesdayPm: boolean;
  wednesdayAm: boolean;
  wednesdayPm: boolean;
  thursdayAm: boolean;
  thursdayPm: boolean;
  fridayAm: boolean;
  fridayPm: boolean;
  saturdayAm: boolean;
  saturdayPm: boolean;
  sundayAm: boolean;
  sundayPm: boolean;
}

const WEEKDAY_AM_KEYS: (keyof SimpleAvailability)[] = [
  'mondayAm',
  'tuesdayAm',
  'wednesdayAm',
  'thursdayAm',
  'fridayAm',
];
const WEEKDAY_PM_KEYS: (keyof SimpleAvailability)[] = [
  'mondayPm',
  'tuesdayPm',
  'wednesdayPm',
  'thursdayPm',
  'fridayPm',
];
const WEEKEND_KEYS: (keyof SimpleAvailability)[] = ['saturdayAm', 'saturdayPm', 'sundayAm', 'sundayPm'];
const ALL_KEYS: (keyof SimpleAvailability)[] = [...WEEKDAY_AM_KEYS, ...WEEKDAY_PM_KEYS, ...WEEKEND_KEYS];

function summariseAvailability(avail: SimpleAvailability | null | undefined): string {
  if (!avail) return 'Not set';
  const weekdayAm = WEEKDAY_AM_KEYS.some((k) => avail[k]);
  const weekdayPm = WEEKDAY_PM_KEYS.some((k) => avail[k]);
  const weekend = WEEKEND_KEYS.some((k) => avail[k]);
  const parts: string[] = [];
  if (weekdayAm) parts.push('Weekday daytime');
  if (weekdayPm) parts.push('Weekday evenings');
  if (weekend) parts.push('Weekends');
  return parts.length > 0 ? parts.join(', ') : 'Not set';
}

type AvailabilityFilter = 'weekday-daytime' | 'weekday-evenings' | 'weekends';

function matchesAvailabilityFilter(avail: SimpleAvailability | null | undefined, filter: AvailabilityFilter): boolean {
  if (!avail) return false;
  if (filter === 'weekday-daytime') return WEEKDAY_AM_KEYS.some((k) => avail[k]);
  if (filter === 'weekday-evenings') return WEEKDAY_PM_KEYS.some((k) => avail[k]);
  return WEEKEND_KEYS.some((k) => avail[k]);
}

function computeCompatibility(
  applicantAvail: SimpleAvailability | null | undefined,
  candidateAvail: SimpleAvailability | null | undefined
): number {
  if (!applicantAvail || !candidateAvail) return 0;
  const applicantSlots = ALL_KEYS.filter((k) => applicantAvail[k]).length;
  if (applicantSlots === 0) return 0;
  const overlap = ALL_KEYS.filter((k) => applicantAvail[k] && candidateAvail[k]).length;
  return overlap / applicantSlots;
}

function getCompatLevel(score: number): 1 | 2 | 3 | 4 | 5 {
  if (score >= 0.75) return 1;
  if (score >= 0.5) return 2;
  if (score >= 0.25) return 3;
  if (score > 0) return 4;
  return 5;
}

const COMPAT_COLORS: Record<number, string> = {
  1: '#2E7D32',
  2: '#7CB342',
  3: '#FBC02D',
  4: '#F57C00',
  5: '#D32F2F',
};

interface MatchingCandidateApplication {
  applicationId: string;
  name: string;
  email: string;
  status: string;
  profile: {
    profileId: string;
    preferredName: string;
    lastName: string;
    dateOfBirth: string | null;
    simpleAvailability: SimpleAvailability | null;
  } | null;
  applicationRoles: Array<{
    applicationRoleId: string;
    role: {
      activityRoleId: string;
      name: string;
    };
    status: string;
  }>;
  funnel: {
    name: string;
  } | null;
}

const useMatchingStyles = () => {
  const { theme } = useCampfireTheme();
  return makeStyles({
    sectionHeading: {
      fontWeight: 700,
      fontSize: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '18px 12px 10px',
      borderBottom: `1px solid ${theme.color.grey.border}`,
    },
    sectionIcon: {
      marginRight: 8,
    },
    subtitle: {
      padding: '10px 12px 0',
      fontSize: 13,
      color: theme.color.grey.neutral300,
    },
    filterBar: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px',
      borderBottom: `1px solid ${theme.color.grey.border}`,
      flexWrap: 'wrap' as const,
      background: '#F9F9F9',
    },
    filterLabel: {
      fontSize: 12,
      color: theme.color.grey.neutral300,
      marginRight: 4,
    },
    filterChip: {
      height: 30,
      fontSize: 13,
      borderRadius: 16,
      border: `1px solid ${theme.color.grey.neutral200}`,
      background: '#FFF',
      cursor: 'pointer',
      '& .MuiChip-label': {
        paddingLeft: 12,
        paddingRight: 8,
      },
      '& .MuiChip-deleteIcon': {
        fontSize: 16,
        marginRight: 4,
      },
    },
    filterChipActive: {
      background: '#E6E5FC',
      borderColor: '#af9dff',
      color: '#635ac7',
      fontWeight: 500,
      '& .MuiChip-deleteIcon': {
        color: '#796fff',
      },
    },
    addFilterChip: {
      height: 30,
      fontSize: 13,
      borderRadius: 16,
      border: `1px dashed ${theme.color.grey.neutral200}`,
      background: 'transparent',
      color: theme.color.grey.neutral300,
      cursor: 'pointer',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        background: '#FBFAFF',
      },
    },
    candidateRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '14px 16px 14px 32px',
      borderBottom: `1px solid ${theme.color.grey.border}`,
      cursor: 'pointer',
      '&:hover': {
        background: '#F5F5F5',
      },
    },
    candidateMain: {
      flex: 1,
      minWidth: 0,
    },
    candidateNameRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap' as const,
    },
    candidateName: {
      fontSize: 15,
      fontWeight: 500,
    },
    candidateFields: {
      display: 'flex',
      alignItems: 'center',
      gap: '18px',
      marginTop: 6,
      flexWrap: 'wrap' as const,
      fontSize: 13,
      color: theme.color.grey.neutral400,
    },
    fieldLabel: {
      color: theme.color.grey.neutral200,
      marginRight: 4,
    },
    fieldValue: {
      fontWeight: 500,
      color: theme.color.grey.neutralBrand800,
    },
    roleChip: {
      height: 22,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      borderRadius: 5,
    },
    compatDot: {
      width: 18,
      height: 18,
      borderRadius: '50%',
      boxShadow: '0 0 0 3px rgba(0,0,0,0.04)',
      flexShrink: 0,
    },
    candidateRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      flexShrink: 0,
    },
    matchBtn: {
      textTransform: 'none' as const,
      fontWeight: 500,
      fontSize: 14,
      borderRadius: 6,
      padding: '10px 20px',
    },
    matchedBtn: {
      textTransform: 'none' as const,
      fontWeight: 500,
      fontSize: 14,
      borderRadius: 6,
      padding: '10px 20px',
      backgroundColor: theme.status.green.medium,
      color: '#FFF',
      '&:hover': {
        backgroundColor: theme.status.green.dark,
      },
    },
    sortBtn: {
      fontSize: 13,
      color: theme.color.grey.neutral300,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      '&:hover': {
        color: theme.color.grey.neutralBrand800,
      },
    },
  })();
};

export interface MatchedCandidate {
  applicationId: string;
  name: string;
  profileId: string;
  preferredName: string;
  lastName: string;
  roleName: string;
  roleId: string;
}

interface MatchingPanelProps {
  applicantName: string;
  applicantRoleId: string;
  applicantRoleName: string;
  applicantProfileId: string;
  onMatchSelect?: (candidate: MatchedCandidate | null) => void;
}

export const MatchingPanel: React.FC<MatchingPanelProps> = ({
  applicantName,
  applicantRoleId,
  applicantRoleName,
  applicantProfileId,
  onMatchSelect,
}) => {
  const classes = useMatchingStyles();
  const { theme } = useCampfireTheme();
  const [matchedId, setMatchedId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<AvailabilityFilter[]>([]);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortAsc, setSortAsc] = useState(false);

  const counterpartRoleId = getCounterpartRoleId(applicantRoleId);

  const { data, loading } = useCampfireQuery<any, any>(GET_MATCHING_CANDIDATES, {
    options: {
      variables: { roleId: counterpartRoleId ?? '' },
      skip: !counterpartRoleId,
    },
  });

  const { data: applicantData } = useCampfireQuery<any, any>(GET_APPLICANT_AVAILABILITY, {
    options: {
      variables: { profileId: applicantProfileId },
    },
  });

  const applicantAvailability: SimpleAvailability | null = applicantData?.vm?.profile?.simpleAvailability ?? null;

  const candidates: MatchingCandidateApplication[] = data?.vm?.matchingCandidates ?? [];

  const filteredAndSorted = useMemo(() => {
    let result = candidates.filter((c) => {
      if (activeFilters.length === 0) return true;
      const avail = c.profile?.simpleAvailability;
      return activeFilters.every((f) => matchesAvailabilityFilter(avail, f));
    });

    result = [...result].sort((a, b) => {
      const scoreA = computeCompatibility(applicantAvailability, a.profile?.simpleAvailability);
      const scoreB = computeCompatibility(applicantAvailability, b.profile?.simpleAvailability);
      return sortAsc ? scoreA - scoreB : scoreB - scoreA;
    });

    return result;
  }, [candidates, activeFilters, applicantAvailability, sortAsc]);

  const handleMatch = (candidate: MatchingCandidateApplication) => {
    const isAlreadyMatched = matchedId === candidate.applicationId;
    const newId = isAlreadyMatched ? null : candidate.applicationId;
    setMatchedId(newId);

    if (onMatchSelect) {
      if (isAlreadyMatched || !candidate.profile) {
        onMatchSelect(null);
      } else {
        const roleName =
          candidate.applicationRoles.find((ar) => ar.role.activityRoleId === counterpartRoleId)?.role.name ??
          'Applicant';
        onMatchSelect({
          applicationId: candidate.applicationId,
          name: `${candidate.profile.preferredName} ${candidate.profile.lastName}`,
          profileId: candidate.profile.profileId,
          preferredName: candidate.profile.preferredName,
          lastName: candidate.profile.lastName,
          roleName,
          roleId: counterpartRoleId ?? '',
        });
      }
    }
  };

  const toggleFilter = (filter: AvailabilityFilter) => {
    setActiveFilters((prev) => (prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]));
  };

  const availableFilters: { key: AvailabilityFilter; label: string }[] = [
    { key: 'weekday-daytime', label: 'Weekday daytime' },
    { key: 'weekday-evenings', label: 'Weekday evenings' },
    { key: 'weekends', label: 'Weekends' },
  ];

  if (loading) {
    return (
      <Box padding={4} display='flex' justifyContent='center'>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!counterpartRoleId || candidates.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box className={classes.sectionHeading}>
        <Box display='flex' alignItems='center'>
          <CompareArrowsIcon
            className={classes.sectionIcon}
            style={{ color: theme.palette.primary.main, fontSize: 22 }}
          />
          <span>Find a Match</span>
        </Box>
        <Box className={classes.sortBtn} onClick={() => setSortAsc((prev) => !prev)}>
          <SortIcon style={{ fontSize: 18 }} />
          Sort: Compatibility ({sortAsc ? 'lowest' : 'highest'})
        </Box>
      </Box>

      <Typography className={classes.subtitle}>
        Showing candidates compatible with{' '}
        <strong style={{ color: theme.color.grey.neutralBrand800 }}>{applicantName}</strong> ({applicantRoleName}).
        {` ${filteredAndSorted.length} of ${candidates.length} candidate${candidates.length !== 1 ? 's' : ''}.`}
      </Typography>

      <Box className={classes.filterBar}>
        <span className={classes.filterLabel}>Filter</span>
        {activeFilters.map((filter) => {
          const label = availableFilters.find((f) => f.key === filter)?.label ?? filter;
          return (
            <Chip
              key={filter}
              label={label}
              size='small'
              className={`${classes.filterChip} ${classes.filterChipActive}`}
              onDelete={() => toggleFilter(filter)}
              deleteIcon={<CloseIcon />}
            />
          );
        })}
        <Chip
          icon={<AddIcon style={{ fontSize: 16 }} />}
          label='Add filter'
          size='small'
          className={classes.addFilterChip}
          onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
          variant='outlined'
          style={{ border: 'none' }}
        />
        <Menu anchorEl={filterMenuAnchor} open={Boolean(filterMenuAnchor)} onClose={() => setFilterMenuAnchor(null)}>
          {availableFilters
            .filter((f) => !activeFilters.includes(f.key))
            .map((f) => (
              <MenuItem
                key={f.key}
                onClick={() => {
                  toggleFilter(f.key);
                  setFilterMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <ScheduleIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary={f.label} />
              </MenuItem>
            ))}
          {availableFilters.filter((f) => !activeFilters.includes(f.key)).length === 0 && (
            <MenuItem disabled>
              <ListItemText primary='All filters active' />
            </MenuItem>
          )}
        </Menu>
      </Box>

      <Box>
        {filteredAndSorted.map((candidate) => {
          const isMatched = matchedId === candidate.applicationId;
          const age = calculateAge(candidate.profile?.dateOfBirth);
          const displayName = candidate.profile
            ? `${candidate.profile.preferredName} ${candidate.profile.lastName}`
            : candidate.name;
          const roleName =
            candidate.applicationRoles.find((ar) => ar.role.activityRoleId === counterpartRoleId)?.role.name ??
            'Applicant';
          const availSummary = summariseAvailability(candidate.profile?.simpleAvailability);
          const compatScore = computeCompatibility(applicantAvailability, candidate.profile?.simpleAvailability);
          const compatLevel = getCompatLevel(compatScore);

          return (
            <Box key={candidate.applicationId} className={classes.candidateRow}>
              <FallbackAvatar initials={getInitials(displayName)} size={40} />

              <Box className={classes.candidateMain}>
                <Box className={classes.candidateNameRow}>
                  <Typography className={classes.candidateName}>{displayName}</Typography>
                  <Chip
                    label={roleName.toUpperCase()}
                    size='small'
                    className={classes.roleChip}
                    style={{
                      backgroundColor: theme.color.grey.neutralBrand100,
                      color: theme.color.grey.neutral400,
                    }}
                  />
                  {candidate.funnel && (
                    <Chip
                      label={candidate.funnel.name}
                      size='small'
                      variant='outlined'
                      className={classes.roleChip}
                      style={{ color: theme.color.grey.neutral400 }}
                    />
                  )}
                </Box>

                <Box className={classes.candidateFields}>
                  {age !== null && (
                    <span>
                      <span className={classes.fieldLabel}>Age:</span>
                      <span className={classes.fieldValue}>{age}</span>
                    </span>
                  )}
                  <span>
                    <span className={classes.fieldLabel}>Avail:</span>
                    <span className={classes.fieldValue}>{availSummary}</span>
                  </span>
                </Box>
              </Box>

              <Box className={classes.candidateRight}>
                <Box
                  className={classes.compatDot}
                  style={{ backgroundColor: COMPAT_COLORS[compatLevel] }}
                  title={`Compatibility: ${Math.round(compatScore * 100)}%`}
                />
                {isMatched ? (
                  <Button
                    variant='contained'
                    className={classes.matchedBtn}
                    disableElevation
                    startIcon={<CheckIcon style={{ fontSize: 16 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMatch(candidate);
                    }}
                  >
                    Matched
                  </Button>
                ) : (
                  <Button
                    variant='contained'
                    color='primary'
                    className={classes.matchBtn}
                    disableElevation
                    startIcon={<LinkIcon style={{ fontSize: 16 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMatch(candidate);
                    }}
                  >
                    Match
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
        {filteredAndSorted.length === 0 && (
          <Box padding='24px 32px' textAlign='center'>
            <Typography style={{ fontSize: 14, color: theme.color.grey.neutral300 }}>
              No candidates match the current filters.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
